import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { AcknowledgeAlarmRequest, MarkFalseAlarmRequest } from "@opus-dt/shared-types";
import type { AlarmSummary } from "../../types/alarms.types.js";

export type { AlarmSummary } from "../../types/alarms.types.js";

interface ListParams {
  includeSuppressed?: boolean;
  includeResolved?: boolean;
}

const QK = ["alarms"] as const;

export function useAlarms(params: ListParams = {}, refetchInterval = 30_000) {
  const search = new URLSearchParams();
  if (params.includeSuppressed) search.set("includeSuppressed", "true");
  if (params.includeResolved) search.set("includeResolved", "true");
  const qs = search.toString();
  return useQuery<AlarmSummary[]>({
    queryKey: [...QK, params],
    queryFn: () => api.get<AlarmSummary[]>(`/api/v1/alarms${qs ? `?${qs}` : ""}`),
    refetchInterval,
  });
}

function invalidateAlarmsAfterMutation(
  qc: ReturnType<typeof useQueryClient>,
  result: AlarmSummary | undefined
) {
  void qc.invalidateQueries({ queryKey: QK });
  // Alarmes agora alimentam a visão de atenção unificada (card + sidebar +
  // /alarms, queryKey ["attention",...]). Toda mutação de alarme DEVE invalidá-la aqui,
  // no helper, para todo call site herdar (inclui AckAlarmInline da sidebar).
  void qc.invalidateQueries({ queryKey: ["attention"] });
  const lineId = result?.machine?.lineId;
  if (lineId) {
    void qc.invalidateQueries({ queryKey: ["line-alarms", lineId] });
  }
}

export function useAcknowledgeAlarm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AcknowledgeAlarmRequest }) =>
      api.patch<AlarmSummary>(`/api/v1/alarms/${id}/acknowledge`, data),
    onSuccess: (data) => invalidateAlarmsAfterMutation(qc, data),
    // Todos os call sites (AckAlarmInline, AckDialog, NotificationBell) tratam erro localmente.
    meta: { skipErrorToast: true },
  });
}

export function useResolveAlarm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.patch<AlarmSummary>(`/api/v1/alarms/${id}/resolve`),
    onSuccess: (data) => invalidateAlarmsAfterMutation(qc, data),
    meta: { skipErrorToast: true }, // pages/Alarms/index.tsx já trata erro localmente
  });
}

export function useLineAlarms(lineId: string, refetchInterval = 30_000) {
  return useQuery<AlarmSummary[]>({
    queryKey: ["line-alarms", lineId],
    queryFn: () =>
      api
        .get<AlarmSummary[]>("/api/v1/alarms?includeSuppressed=true")
        .then((all) => all.filter((a) => a.machine?.lineId === lineId)),
    enabled: !!lineId,
    refetchInterval,
  });
}

export function useMarkFalseAlarm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MarkFalseAlarmRequest }) =>
      api.patch<AlarmSummary>(`/api/v1/alarms/${id}/mark-false`, data),
    onSuccess: (data) => invalidateAlarmsAfterMutation(qc, data),
    meta: { skipErrorToast: true }, // pages/Alarms/MarkFalseDialog.tsx já trata erro localmente
  });
}
